import { resolutionSchema } from '@willie/schemas'
import { z } from 'zod'

export type BudgetItemSchema = z.infer<typeof budgetItemSchema>
export const budgetItemSchema = z.object({
  id: z.string().uuid(),
  siteID: z.string().uuid(),
  startDate: z.coerce.date(),
  endDate: z.coerce.date(),
  consumption: z.number(),
  cost: z.number(),
  type: z.enum(['historical', 'objective'])
})

export type HistoricalInvoiceSchema = z.infer<typeof historicalInvoiceSchema>
export const historicalInvoiceSchema = budgetItemSchema
  .omit({ type: true })
  .extend({ type: z.literal('historical') })

export type ObjectiveBudgetItemSchema = z.infer<typeof objectiveBudgetItemSchema>
export const objectiveBudgetItemSchema = budgetItemSchema
  .omit({ type: true })
  .extend({ type: z.literal('objective') })

export type CreateBudgetItemSchema = z.infer<typeof createBudgetItemSchema>
export const createBudgetItemSchema = budgetItemSchema.omit({ id: true })

export type BudgetItemType = BudgetItemSchema['type']

const budgetObjectiveMeasure = z.enum(['volume', 'currency'])

const baseGoalSchema = z.object({
  id: z.string().uuid(),
  startDate: z.coerce.date(),
  endDate: z.coerce.date(),
  goal: z.number(),
  measure: budgetObjectiveMeasure,
  metricID: z.string().uuid().optional(),
  stationID: z.string().uuid().optional(),
  resolution: resolutionSchema
})

export const createBudgetGoalSchema = baseGoalSchema
  .omit({ id: true })
  .refine((data) => data.metricID !== undefined || data.stationID !== undefined, {
    message: 'At least one of metricID or stationID must be provided',
    path: ['metricID', 'stationID'] // highlights both fields in error messages
  })

export const goalSchema = baseGoalSchema.extend({
  metric: z
    .object({
      id: z.string().uuid(),
      name: z.string()
    })
    .optional(),
  station: z
    .object({
      id: z.string().uuid(),
      name: z.string()
    })
    .optional()
})

export type BudgetGoalSchema = z.infer<typeof goalSchema>
export type CreateBudgetGoalSchema = z.infer<typeof createBudgetGoalSchema>
